import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Col, Form, Row, Select } from 'antd';

import type IProduct from 'interfaces/IProduct';
import { FormContent } from 'scenes/Producer/Product/EditProduct/components/FormContent';
import { useDeliveryIntegrations } from 'v2/hooks/products/useDeliveryIntegrations';
import { EChargeType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import productService from 'v2/services/product';
import { usePhysicalDeliveryStore } from 'v2/store/PhysicalDelivery';

import Correios from './Form/Correios';
import FixedValue from './Form/FixedValue';
import Free from './Form/Free';
import MelhorEnvio from './Form/MelhorEnvio';
import Partner from './Form/Partner';
import type { IrregularProduct } from '../../MyProducts/constants';

const FREE = '1';
const FIXEDVALUE = '2';
const CORREIOS = '3';
const MELHORENVIO = '4';

interface IProps {
  enabledValidation?: boolean;
  onAddPartner?: any;
  onRemovePartner?: any;
  onChangeRequiredFields?: any;
  loading?: boolean;
  onEdit?: any;
  ref?: any;
  fade?: any;
  setRefField?: any;
  product?: IProduct;
}

const PhysicalDeliveryMethods: React.FC<IProps> = ({
  enabledValidation,
  onAddPartner,
  onRemovePartner,
  onChangeRequiredFields,
  loading,
  onEdit,
  product
}) => {
  const { delivery, setDelivery } = usePhysicalDeliveryStore();
  const { integrations: integration, refetch: refetchIntegrations } = useDeliveryIntegrations();
  const [pendingActions, setPendingActions] = useState<IrregularProduct['pending_actions']>(null);
  const prevDeliveryRef = useRef(delivery);
  const isSubscription = product?.charge_type === EChargeType.SUBSCRIPTION;

  const changeState = useCallback(() => {
    if (onChangeRequiredFields) {
      onChangeRequiredFields();
    }
  }, [onChangeRequiredFields]);

  const getIntegrations = useCallback(() => {
    refetchIntegrations();
  }, [refetchIntegrations]);

  const handleChange = useCallback(
    (prop: string, value: any) => {
      if (prop === 'delivery_type' && value === MELHORENVIO) {
        getIntegrations();
      }

      setDelivery({ ...delivery, [prop]: value });
    },
    [delivery, setDelivery, getIntegrations]
  );

  const getActionsMessage = useCallback((actions: IrregularProduct['pending_actions']) => {
    const messages = [];

    if (actions.invalid_physical_dimensions) {
      messages.push(
        <div className='pb-2' key='invalid-dimensions'>
          <Alert
            type='info'
            message={
              <>
                <b>Ajuste necessário nas dimensões dos produtos físicos</b>
                <br />
                Devido às restrições de dimensões de embalagens estabelecidas pelos Correios, é necessário que você
                ajuste as dimensões de seus produtos até <b>31/10/2024</b>. Após essa data, faremos o ajuste automático,
                aplicando as dimensões máximas permitidas pelos Correios. Os valores máximos estão especificados nos
                campos. Sem essa ação, o checkout será impactado e suas vendas poderão ser afetadas.
              </>
            }
          />
        </div>
      );
    }

    return messages;
  }, []);

  const loadIrregularProduct = useCallback(async () => {
    const productId = String(product.id);
    const result = await productService.listIrregularProducts({ per_page: 20, current_page: 1 }, productId);
    const { data } = result;

    if (!!data.length) {
      setPendingActions(data[0].pending_actions);
    }
  }, [product?.id]);

  useEffect(() => {
    if (!loading && enabledValidation) {
      changeState();
    }

    loadIrregularProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && delivery !== prevDeliveryRef.current) {
      changeState();
    }

    prevDeliveryRef.current = delivery;
  }, [delivery, loading, changeState]);

  return (
    <FormContent title='Entrega' loading={false}>
      <Row gutter={[16, 10]}>
        {pendingActions && <Col span={24}>{getActionsMessage(pendingActions)}</Col>}
        <Col span={24} className='mb-2'>
          <SelectType delivery={delivery} product={product} onChange={handleChange} />
        </Col>
        {!isSubscription && delivery?.delivery_type == CORREIOS && (
          <Correios onEdit={onEdit} enabledValidation={enabledValidation} />
        )}
        {!isSubscription && delivery?.delivery_type == MELHORENVIO && (
          <MelhorEnvio
            onEdit={onEdit}
            integration={{
              logisticType: integration?.logisticType,
              status: integration?.status
            }}
          />
        )}
        {delivery.delivery_type == FREE && <Free onEdit={onEdit} enabledValidation={enabledValidation} />}
        {delivery.delivery_type == FIXEDVALUE && <FixedValue onEdit={onEdit} enabledValidation={enabledValidation} />}
        {!delivery.new && <Partner onEdit={onEdit} onAddPartner={onAddPartner} onRemovePartner={onRemovePartner} />}
      </Row>
    </FormContent>
  );
};

const SelectType = ({
  delivery,
  product,
  onChange
}: {
  delivery: any;
  product: IProduct;
  onChange: (field: string, value: string) => void;
}) => {
  const isBeta = eduzzAccountsService.verifyUserHasBetaModule('delivery_melhor_envio');
  const isSubscription = product?.charge_type === EChargeType.SUBSCRIPTION;

  const options = [
    ...(!isSubscription ? [{ label: 'Correios', value: CORREIOS }] : []),
    ...(!isSubscription && isBeta ? [{ label: 'Melhor Envio', value: MELHORENVIO }] : []),
    { label: 'Frete Grátis', value: FREE },
    { label: 'Valor Fixo', value: FIXEDVALUE }
  ];

  return (
    <Form.Item label='Tipo de frete'>
      <Select
        id='delivery_type'
        className='w-full'
        value={delivery?.delivery_type ? String(delivery.delivery_type) : undefined}
        onChange={(value: string) => onChange('delivery_type', value)}
        options={options}
      />
    </Form.Item>
  );
};

export default PhysicalDeliveryMethods as any;
